/**
 * Calculate position for toast element in some place
 *
 * @param toastRect - toast element size and coordinates
 * @param anchorRect - anchor element size and coordinates
 * @param place - place for toast element
 * @param placementOffsets - additional offsets
 */
function calculateToastPosition(_a) {
    var toastRect = _a.toastRect, anchorRect = _a.anchorRect, place = _a.place, placementOffsets = _a.placementOffsets;
    var position = {
        left: 0,
        top: 0,
        placement: place,
    };
    switch (place) {
        case 'top':
            position.left = anchorRect.left + anchorRect.width - toastRect.width;
            position.top = anchorRect.top - toastRect.height;
            break;
        case 'bottom':
            position.left = anchorRect.left + anchorRect.width - toastRect.width;
            position.top = anchorRect.bottom;
            break;
        case 'right':
            position.left = anchorRect.right;
            position.top = anchorRect.top + anchorRect.height / 2 - toastRect.height / 2;
            break;
        case 'left':
            position.left = anchorRect.left - toastRect.width;
            position.top = anchorRect.top + anchorRect.height / 2 - toastRect.height / 2;
            break;
    }
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    if (placementOffsets !== undefined && placementOffsets[place] !== undefined) {
        position.top += placementOffsets[place].top;
        position.left += placementOffsets[place].left;
    }
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
    position.top += window.pageYOffset;
    position.left += window.pageXOffset;
    return position;
}
/**
 * Find an optimal toast element position
 *
 * @param toastRect - popup element size and coordinates
 * @param anchorRect - anchor element size and coordinates
 * @param places - preferred places for popup element
 * @param placementOffsets - additional offsets
 * @param viewport - display frame
 */
function autoToastPosition(_a) {
    var toastRect = _a.toastRect, anchorRect = _a.anchorRect, places = _a.places, placementOffsets = _a.placementOffsets, _b = _a.viewport, viewport = _b === void 0 ? {
        top: window.scrollY,
        bottom: window.scrollY + window.innerHeight,
        left: window.scrollX,
        right: window.scrollX + window.innerWidth,
    } : _b;
    var pos;
    if (places.length === 0) {
        throw new Error('You have to send at least one place!');
    }
    for (var _i = 0, places_1 = places; _i < places_1.length; _i++) {
        var place = places_1[_i];
        pos = calculateToastPosition({ toastRect: toastRect, anchorRect: anchorRect, place: place, placementOffsets: placementOffsets });
        if (pos.left >= viewport.left &&
            pos.top >= viewport.top &&
            pos.left + toastRect.width <= viewport.right &&
            pos.top + toastRect.height <= viewport.bottom) {
            break;
        }
    }
    return pos;
}
/**
 * Remove element when his out animation is ended
 * @param e - custom event, returns animation name in detail property
 */
function handleAnimationEndEvent(e) {
    if (e.detail === 'notify-animation-out') {
        e.target.removeEventListener('isAnimated', handleAnimationEndEvent);
        e.target.remove();
    }
}
/**
 * Function for creating toast element and mount into DOM
 * @param toastContent - HTML element, will be inserted into toast
 * @param mountTarget - DOM node element, where toast will be mounted, default: document.body
 * @param visibilityTimeout - timeout before toast will be removed from DOM, default: 2000 milliseconds
 */
function createToast(_a) {
    var toastContent = _a.toastContent, timeout = _a.timeout, zIndex = _a.zIndex, _b = _a.mountTarget, mountTarget = _b === void 0 ? document.body : _b;
    var notify = document.createElement('ispui-toast-popup');
    notify.setAttribute('content', toastContent);
    zIndex && notify.setAttribute('z-index', zIndex);
    mountTarget.appendChild(notify);
    window.setTimeout(function () {
        notify.classList.add('ispui-toast-popup_close');
    }, timeout);
    notify.addEventListener('isAnimated', handleAnimationEndEvent);
    return notify;
}
/**
 * Method for calculate toast position
 * @param toastRect - size and coords of toast element
 * @param anchorRect - size and coords of anchor element
 * @param offsets - additional offsets
 * @param placement - place for toast element
 * @param viewport - display frame
 */
function calculatePositionCoords(_a) {
    var toastRect = _a.toastRect, anchorRect = _a.anchorRect, offsets = _a.offsets, placement = _a.placement, viewport = _a.viewport;
    var toastPosition;
    if (!placement) {
        toastPosition = autoToastPosition({
            toastRect: toastRect,
            anchorRect: anchorRect,
            places: ['right', 'bottom', 'top'],
            placementOffsets: offsets,
            viewport: viewport,
        });
    }
    else {
        toastPosition = calculateToastPosition({ toastRect: toastRect, anchorRect: anchorRect, place: placement, placementOffsets: offsets });
    }
    return toastPosition;
}
/**
 * Set position coordinates for toast element
 * @param toastElement - toast popup element
 * @param toastCoordinates - toast popup coordinates
 * @param position - css position of toast popup element
 */
function setPositionForToast(toastElement, toastCoordinates, position) {
    if (position === void 0) { position = 'absolute'; }
    toastElement.setAttribute('position', position);
    Object.keys(toastCoordinates).forEach(function (key) {
        if (toastCoordinates[key]) {
            toastElement.setAttribute(key, toastCoordinates[key] + '');
        }
    });
}
/**
 * Method for showing toast
 * @param toastContent - HTML element, will be inserted into toast
 * @param anchorElement - anchor HTML element
 * @param placement - toast placement
 * @param offsets - toast offsets
 * @param viewport - display frame
 * @param mountTarget - DOM node element, where toast will be mounted, default: document.body
 * @param visibilityTimeout - timeout before toast will be removed from DOM, default: 2000 milliseconds
 */
function showToast(_a) {
    var toastContent = _a.toastContent, anchorElement = _a.anchorElement, _b = _a.timeout, timeout = _b === void 0 ? 2000 : _b, placement = _a.placement, offsets = _a.offsets, zIndex = _a.zIndex, viewport = _a.viewport, _c = _a.mountTarget, mountTarget = _c === void 0 ? document.body : _c, position = _a.position;
    if (!offsets) {
        offsets = {
            top: {
                left: 0,
                top: -10,
            },
            bottom: {
                left: 0,
                top: 10,
            },
            right: {
                left: 10,
                top: 0,
            },
        };
    }
    var toastElement = createToast({ toastContent: toastContent, timeout: timeout, zIndex: zIndex, mountTarget: mountTarget });
    toastElement.addEventListener('isLoaded', function (e) {
        var toastRect = e.detail;
        var toastPosition = calculatePositionCoords({
            toastRect: toastRect,
            anchorRect: anchorElement.getBoundingClientRect(),
            offsets: offsets,
            placement: placement,
            viewport: viewport,
        });
        setPositionForToast(toastElement, toastPosition, position);
    });
    return toastElement;
}
export { showToast as s };
